import { useState } from 'react';
import { Plus } from 'lucide-react';
import { createCrmLeadTask, createCrmOrderTask, type CrmTask } from '../../../api';
import { CRM_TASK_KIND_LABELS } from './crmLabels';
import { useInvalidateCrm } from '../../../hooks/crm/useCrmQueries';
import { CrmTaskDueFields, defaultDueParts, mergeDueLocal } from './CrmTaskDueFields';

type Props = {
  orderId?: number;
  leadId?: number;
  onCreated: (task: CrmTask) => void;
  hapticSelection: () => void;
};

function defaultDueIso(): string {
  const p = defaultDueParts();
  return mergeDueLocal(p.date, p.time);
}

export function CrmTaskForm({ orderId, leadId, onCreated, hapticSelection }: Props) {
  const invalidate = useInvalidateCrm();
  const [title, setTitle] = useState('Связаться с клиентом');
  const [kind, setKind] = useState('call');
  const [dueAt, setDueAt] = useState(defaultDueIso);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim() || !dueAt || (!orderId && !leadId)) return;
    hapticSelection();
    setSaving(true);
    try {
      const dueIso = dueAt;
      const payload = { title: title.trim(), kind, due_at: dueIso };
      const task = orderId
        ? await createCrmOrderTask(orderId, payload)
        : await createCrmLeadTask(leadId!, payload);
      onCreated(task);
      void invalidate();
      setTitle('Связаться с клиентом');
      setDueAt(defaultDueIso());
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      id="crm-deal-task-form"
      className="space-y-2 p-3 rounded-xl"
      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}
    >
      <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--text-3)' }}>
        Новая задача
      </p>
      <input
        className="input-glass w-full text-sm min-h-[40px] touch-manipulation"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Что сделать"
      />
      <select
        className="input-glass w-full text-sm min-h-[40px] touch-manipulation"
        value={kind}
        onChange={(e) => setKind(e.target.value)}
      >
        {Object.entries(CRM_TASK_KIND_LABELS).map(([k, label]) => (
          <option key={k} value={k}>
            {label}
          </option>
        ))}
      </select>
      <CrmTaskDueFields value={dueAt} onChange={setDueAt} disabled={saving} />
      <button
        type="button"
        disabled={saving}
        onClick={() => void submit()}
        className="press w-full flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-bold touch-manipulation min-h-[44px]"
        style={{ background: 'rgba(99,179,237,0.2)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.35)' }}
      >
        <Plus size={14} /> {saving ? '…' : 'Добавить задачу'}
      </button>
    </div>
  );
}
